/**
 * TimetableGrid Component
 *
 * Weekly timetable grid showing all scheduled sessions.
 * Displays sessions as colored blocks with their time ranges.
 */

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useTimetableSessions, useSessionTypes, useTimetableMutations } from '@/hooks/useTimetable'
import { useTimetableWeek, getMonday } from '@/hooks/useTimetableWeek'
import SessionDialog from './SessionDialog'
import { Plus, Edit, Trash2, Clock, MapPin, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react'
import { format, addDays } from 'date-fns'
import { toLocalDateString } from '@/utils/dateUtils'

const DAYS = [
  { value: 0, label: 'Monday', short: 'Mon' },
  { value: 1, label: 'Tuesday', short: 'Tue' },
  { value: 2, label: 'Wednesday', short: 'Wed' },
  { value: 3, label: 'Thursday', short: 'Thu' },
  { value: 4, label: 'Friday', short: 'Fri' },
  { value: 5, label: 'Saturday', short: 'Sat' },
  { value: 6, label: 'Sunday', short: 'Sun' }
]

function CapacityBar({ capacity }) {
  if (!capacity) return null
  const { total_tasks = 0, max_daily_tasks = 20, utilisation_percent = 0, status = 'green' } = capacity
  const colorMap = { green: 'bg-green-500', amber: 'bg-amber-500', red: 'bg-red-500' }
  const fillWidth = Math.min(100, utilisation_percent)

  return (
    <div className="mt-2 px-1">
      <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-0.5">
        <span>{total_tasks}/{max_daily_tasks} tasks</span>
        <span>{Math.round(utilisation_percent)}%</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${colorMap[status] || colorMap.green}`}
          style={{ width: `${fillWidth}%` }}
        />
      </div>
    </div>
  )
}

function TaskCard({ task }) {
  return (
    <div className="rounded border bg-card p-1.5 text-xs">
      <div className="font-medium truncate">{task.task_name}</div>
      <div className="flex items-center gap-1 text-muted-foreground mt-0.5">
        <span className="font-mono">{task.batch_code}</span>
        <span>{task.strain_code}</span>
      </div>
      <div className="flex items-center gap-1 mt-0.5">
        {task.priority_rank <= 3 && (
          <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5">P{task.priority_rank}</Badge>
        )}
        {task.is_overdue && (
          <Badge variant="destructive" className="text-[9px] px-1 py-0 h-3.5">overdue</Badge>
        )}
      </div>
    </div>
  )
}

export default function TimetableGrid() {
  const { sessions, loading, error, refetch } = useTimetableSessions()
  const { sessionTypes } = useSessionTypes()
  const { deleteSession } = useTimetableMutations()
  const [showDialog, setShowDialog] = useState(false)
  const [selectedSession, setSelectedSession] = useState(null)
  const [selectedDay, setSelectedDay] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [weekStart, setWeekStart] = useState(getMonday())

  const { days: weekData, loading: weekLoading, refetch: refetchWeek } = useTimetableWeek(weekStart)

  const navigateWeek = (delta) => {
    const d = new Date(weekStart)
    d.setDate(d.getDate() + delta * 7)
    setWeekStart(toLocalDateString(d))
  }

  const goToToday = () => setWeekStart(getMonday())

  const weekEnd = addDays(new Date(weekStart), 6)
  const weekLabel = `${format(new Date(weekStart), 'd MMM')} – ${format(weekEnd, 'd MMM yyyy')}`

  // Build task/capacity data keyed by day index (0-6)
  const weekDataByDay = {}
  if (weekData) {
    weekData.forEach(day => {
      weekDataByDay[day.day_of_week] = day
    })
  }

  // Group sessions by day
  const sessionsByDay = DAYS.reduce((acc, day) => {
    acc[day.value] = sessions.filter(s => s.day_of_week === day.value)
    return acc
  }, {})

  const formatTime = (timeStr) => {
    if (!timeStr) return ''
    // timeStr is in format "HH:MM:SS"
    const [hours, minutes] = timeStr.split(':')
    return `${hours}:${minutes}`
  }

  const getTextColor = (hexColor) => {
    // Simple contrast calculation - if color is light, use dark text
    if (!hexColor) return '#111827'
    const hex = hexColor.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000
    return brightness > 155 ? '#111827' : '#ffffff'
  }

  const handleAddSession = (dayOfWeek) => {
    setSelectedSession(null)
    setSelectedDay(dayOfWeek)
    setShowDialog(true)
  }

  const handleEditSession = (session) => {
    setSelectedSession(session)
    setSelectedDay(null)
    setShowDialog(true)
  }

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) {
      return
    }

    setDeletingId(sessionId)
    try {
      await deleteSession(sessionId)
      refetch()
    } catch (err) {
      console.error('Failed to delete session:', err)
    } finally {
      setDeletingId(null)
    }
  }

  const handleDialogSave = () => {
    refetch()
    setShowDialog(false)
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="text-muted-foreground">Loading timetable...</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle>Weekly Timetable</CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateWeek(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>Today</Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateWeek(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground min-w-[160px] text-center">{weekLabel}</span>
              <Button variant="outline" size="sm" onClick={() => { refetch(); refetchWeek() }} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {error && (
            <Alert variant="destructive" className="m-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Desktop Grid View */}
          <div className="hidden sm:block overflow-x-auto">
            <div className="grid grid-cols-7 min-w-max">
              {/* Day headers */}
              {DAYS.map(day => (
                <div
                  key={day.value}
                  className="border-r last:border-r-0 border-b bg-muted/50"
                >
                  <div className="p-4">
                    <div className="font-semibold text-sm">{day.label}</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 h-8 w-full"
                      onClick={() => handleAddSession(day.value)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              ))}

              {/* Session cells + tasks + capacity */}
              {DAYS.map(day => {
                const dayData = weekDataByDay[day.value]
                const dayTasks = dayData?.tasks || []
                const dayCapacity = dayData?.capacity

                return (
                  <div
                    key={`sessions-${day.value}`}
                    className="border-r last:border-r-0 p-3 space-y-2 min-h-[300px] flex flex-col"
                  >
                    {/* Sessions */}
                    <div className="space-y-2 flex-1">
                      {sessionsByDay[day.value]?.length === 0 ? (
                        <div className="text-center text-sm text-muted-foreground py-4">
                          No sessions
                        </div>
                      ) : (
                        sessionsByDay[day.value]?.map(session => (
                          <div
                            key={session.id}
                            className="rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer group relative"
                            style={{
                              backgroundColor: session.session_type.colour || '#9CA3AF',
                              color: getTextColor(session.session_type.colour)
                            }}
                            onClick={() => handleEditSession(session)}
                          >
                            <div className="font-medium text-sm mb-1">
                              {session.session_type.name}
                            </div>
                            <div className="flex items-center gap-1 text-xs opacity-90 mb-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(session.start_time)} - {formatTime(session.end_time)}
                            </div>
                            {session.location && (
                              <div className="flex items-center gap-1 text-xs opacity-90">
                                <MapPin className="h-3 w-3" />
                                {session.location}
                              </div>
                            )}

                            {/* Hover actions */}
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 hover:bg-white/20"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleEditSession(session)
                                }}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 hover:bg-white/20"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteSession(session.id)
                                }}
                                disabled={deletingId === session.id}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Scheduled tasks */}
                    {dayTasks.length > 0 && (
                      <div className="space-y-1 pt-2 border-t">
                        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                          Tasks ({dayTasks.length})
                        </div>
                        <div className="space-y-1 max-h-[200px] overflow-y-auto">
                          {dayTasks.slice(0, 8).map((task, i) => (
                            <TaskCard key={task.task_instance_id || i} task={task} />
                          ))}
                          {dayTasks.length > 8 && (
                            <div className="text-[10px] text-muted-foreground text-center">
                              +{dayTasks.length - 8} more
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Capacity bar */}
                    <CapacityBar capacity={dayCapacity} />
                  </div>
                )
              })}
            </div>
          </div>

          {/* Mobile View */}
          <div className="sm:hidden">
            {DAYS.map(day => {
              const daySessions = sessionsByDay[day.value] || []
              const dayData = weekDataByDay[day.value]
              const dayTasks = dayData?.tasks || []
              const dayCapacity = dayData?.capacity

              return (
                <div key={day.value} className="border-b last:border-b-0">
                  <div className="bg-muted/50 p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{day.label}</h3>
                      {dayTasks.length > 0 && (
                        <Badge variant="outline" className="text-[10px]">{dayTasks.length} tasks</Badge>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleAddSession(day.value)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  <div className="p-3 space-y-2">
                    {daySessions.length === 0 && dayTasks.length === 0 ? (
                      <div className="text-center text-sm text-muted-foreground py-4">
                        No sessions or tasks
                      </div>
                    ) : (
                      <>
                        {daySessions.map(session => (
                          <div
                            key={session.id}
                            className="rounded-lg p-3 shadow-sm"
                            style={{
                              backgroundColor: session.session_type.colour || '#9CA3AF',
                              color: getTextColor(session.session_type.colour)
                            }}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="font-medium">{session.session_type.name}</div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-white/20"
                                  onClick={() => handleEditSession(session)}>
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-white/20"
                                  onClick={() => handleDeleteSession(session.id)} disabled={deletingId === session.id}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 text-sm opacity-90 mb-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(session.start_time)} - {formatTime(session.end_time)}
                            </div>
                            {session.location && (
                              <div className="flex items-center gap-1 text-sm opacity-90">
                                <MapPin className="h-3 w-3" />
                                {session.location}
                              </div>
                            )}
                          </div>
                        ))}

                        {dayTasks.length > 0 && (
                          <div className="space-y-1 pt-2 border-t">
                            {dayTasks.slice(0, 5).map((task, i) => (
                              <TaskCard key={task.task_instance_id || i} task={task} />
                            ))}
                            {dayTasks.length > 5 && (
                              <div className="text-xs text-muted-foreground text-center">
                                +{dayTasks.length - 5} more
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    <CapacityBar capacity={dayCapacity} />
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Session Dialog */}
      <SessionDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        session={selectedSession}
        defaultDay={selectedDay}
        sessionTypes={sessionTypes}
        onSave={handleDialogSave}
      />
    </>
  )
}
